import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD, fmtPct, fmtNum, gainClass, relTime } from "@/lib/format";
import PriceChart from "@/components/PriceChart";
import EquityCurve from "@/components/EquityCurve";
import ActivityFeed from "@/components/ActivityFeed";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";

export default function Dashboard() {
  const [portfolio, setPortfolio] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [trades, setTrades] = useState([]);
  const [risk, setRisk] = useState(null);
  const [heroSymbol, setHeroSymbol] = useState({ symbol: "BTC", asset_type: "crypto" });

  useEffect(() => {
    // Poll open limit/stop orders whenever dashboard mounts
    api.post("/orders/check").catch(() => {});
    api.get("/risk/meter").then(({ data }) => setRisk(data)).catch(() => {});
    (async () => {
      const [{ data: p }, { data: w }, { data: t }] = await Promise.all([
        api.get("/portfolio"),
        api.get("/watchlist"),
        api.get("/trades", { params: { limit: 5 } }),
      ]);
      setPortfolio(p);
      setWatchlist(w);
      setTrades(t);
      if (p?.positions?.length) {
        setHeroSymbol({ symbol: p.positions[0].symbol, asset_type: p.positions[0].asset_type });
      }
    })().catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      {/* Header */}
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Portfolio value</p>
          <p
            data-testid="portfolio-total-value"
            className="font-display text-5xl md:text-6xl font-medium tabular-nums tracking-tight mt-2"
          >
            {portfolio ? fmtUSD(portfolio.total_value) : "—"}
          </p>
          {portfolio && (
            <p
              data-testid="portfolio-total-pnl"
              className={`text-lg font-medium tabular-nums mt-2 ${gainClass(portfolio.total_pnl)}`}
            >
              {portfolio.total_pnl >= 0 ? "+" : ""}
              {fmtUSD(portfolio.total_pnl)} ({fmtPct(portfolio.total_pnl_pct)}) all-time
            </p>
          )}

          <Card className="mt-6 border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Equity curve · 30 days</p>
            </div>
            <EquityCurve days={30} height={220} />
          </Card>

          <Card className="mt-6 border border-gray-200 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Featured</p>
                <p className="font-display text-2xl font-medium mt-1">
                  {heroSymbol.symbol} <span className="text-gray-400 text-base">({heroSymbol.asset_type})</span>
                </p>
              </div>
              <Link
                to={`/trade?symbol=${heroSymbol.symbol}&type=${heroSymbol.asset_type}`}
                className="text-sm font-medium bg-brand text-white rounded-full px-4 py-2 hover:bg-brand-deep transition-transform active:scale-95"
              >
                Trade
              </Link>
            </div>
            <PriceChart symbol={heroSymbol.symbol} assetType={heroSymbol.asset_type} days={7} height={240} />
          </Card>
        </div>

        {/* Right column */}
        <div className="lg:col-span-4 space-y-6">
          <Card className="border border-gray-200 rounded-2xl p-6">
            <div className="grid grid-cols-2 gap-4">
              <Stat label="Cash" value={portfolio ? fmtUSD(portfolio.cash_balance) : "—"} testId="stat-cash" />
              <Stat label="Positions" value={portfolio ? fmtUSD(portfolio.market_value) : "—"} testId="stat-positions" />
              <Stat
                label="Unrealized"
                value={portfolio ? fmtUSD(portfolio.unrealized_pnl) : "—"}
                cls={portfolio ? gainClass(portfolio.unrealized_pnl) : ""}
                testId="stat-unrealized"
              />
              <Stat label="Trades" value={String(trades.length ? trades.length : 0)} testId="stat-trades" />
            </div>
          </Card>

          {risk && (
            <Card className="border border-gray-200 rounded-2xl p-6" data-testid="risk-meter-card">
              <div className="flex items-center justify-between mb-4">
                <p className="font-display font-semibold text-lg">Risk meter</p>
                {risk.trading_paused && (
                  <span className="text-[10px] uppercase font-bold rounded-full bg-loss-soft text-loss px-2 py-0.5" data-testid="risk-paused-badge">
                    trading paused
                  </span>
                )}
              </div>
              <RiskBar
                label="Margin in use"
                testId="risk-margin-bar"
                pct={Math.min(risk.margin_utilization_pct, 100)}
                warnAt={25}
                dangerAt={40}
                left={`${fmtUSD(risk.margin_used)} of ${fmtUSD(risk.cash_balance)} cash`}
                right={`${risk.margin_utilization_pct}%`}
              />
              <RiskBar
                label={`Daily loss vs ${risk.max_daily_loss_pct}% limit`}
                testId="risk-dailyloss-bar"
                pct={risk.daily_loss_used_pct}
                warnAt={50}
                dangerAt={80}
                left={
                  <span className={gainClass(risk.daily_realized_pnl)}>
                    {risk.daily_realized_pnl >= 0 ? "+" : ""}
                    {fmtUSD(risk.daily_realized_pnl)} today
                  </span>
                }
                right={`cap ${fmtUSD(risk.daily_loss_cap)}`}
              />
            </Card>
          )}

          <Card className="border border-gray-200 rounded-2xl p-6" data-testid="watchlist-card">
            <div className="flex items-center justify-between mb-4">
              <p className="font-display font-semibold text-lg">Watchlist</p>
              <Link to="/markets" className="text-xs text-gray-500 hover:text-brand-deep">
                Browse markets →
              </Link>
            </div>
            {watchlist.length === 0 ? (
              <p className="text-sm text-gray-500">
                Nothing here yet. Add assets from the Markets page.
              </p>
            ) : (
              <ul className="space-y-3">
                {watchlist.map((w) => (
                  <li key={w.id} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{w.symbol}</p>
                      <p className="text-xs text-gray-500 capitalize">{w.asset_type}</p>
                    </div>
                    <p className="text-sm font-mono">{fmtUSD(w.price, { dp: w.price > 10 ? 2 : 4 })}</p>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="border border-gray-200 rounded-2xl p-6" data-testid="feed-card">
            <div className="flex items-center justify-between mb-4">
              <p className="font-display font-semibold text-lg">Activity</p>
              <Link to="/leaderboard" className="text-xs text-gray-500 hover:text-brand-deep">
                Find traders →
              </Link>
            </div>
            <ActivityFeed limit={10} />
          </Card>
        </div>
      </div>

      {/* Positions & trades */}
      <div className="grid lg:grid-cols-2 gap-8">
        <Card className="border border-gray-200 rounded-2xl p-6" data-testid="positions-card">
          <p className="font-display font-semibold text-lg mb-4">Positions</p>
          {portfolio?.positions?.length ? (
            <div className="divide-y divide-gray-100">
              {portfolio.positions.map((p) => (
                <div key={`${p.symbol}-${p.asset_type}`} className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium">
                      {p.symbol}
                      {p.direction && (
                        <span
                          className={`ml-2 text-[9px] uppercase font-bold tracking-wider rounded-full px-1.5 py-0.5 ${
                            p.direction === "long" ? "bg-gain-soft text-gain" : "bg-loss-soft text-loss"
                          }`}
                          data-testid={`position-direction-${p.symbol}`}
                        >
                          {p.direction}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {fmtNum(p.quantity)} @ {fmtUSD(p.avg_price, { dp: p.avg_price > 10 ? 2 : 4 })}
                      {p.asset_type === "future" && p.margin_held ? ` · margin ${fmtUSD(p.margin_held)}` : ""}
                      {p.asset_type === "future" && p.days_to_expiration != null && p.days_to_expiration <= 7 && (
                        <span className="ml-1 text-amber-600 font-semibold" data-testid={`rollover-warn-${p.symbol}`}>
                          · rolls in {p.days_to_expiration}d
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono tabular-nums">{fmtUSD(p.market_value)}</p>
                    <p className={`text-xs tabular-nums ${gainClass(p.unrealized_pnl)}`}>
                      {fmtPct(p.unrealized_pnl_pct)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No positions yet. Time to make your first trade.</p>
          )}
        </Card>

        <Card className="border border-gray-200 rounded-2xl p-6" data-testid="recent-trades-card">
          <div className="flex items-center justify-between mb-4">
            <p className="font-display font-semibold text-lg">Recent trades</p>
            <Link to="/trade" className="text-xs text-gray-500 hover:text-brand-deep">
              Trade →
            </Link>
          </div>
          {trades.length ? (
            <div className="divide-y divide-gray-100">
              {trades.map((t) => (
                <div key={t._id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    {t.side === "buy" ? (
                      <ArrowUpRight size={16} className="text-gain" />
                    ) : (
                      <ArrowDownRight size={16} className="text-loss" />
                    )}
                    <div>
                      <p className="font-medium text-sm">
                        {t.side.toUpperCase()} {fmtNum(t.quantity)} {t.symbol}
                      </p>
                      <p className="text-xs text-gray-500">{relTime(t.created_at)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-mono">{fmtUSD(t.total)}</p>
                    {t.grade ? (
                      <Badge variant="secondary" className="text-[10px] mt-1">
                        <Sparkles size={10} className="mr-1" />
                        Grade {t.grade}
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-gray-400">Not graded</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No trades yet. Head to Trade to start.</p>
          )}
        </Card>
      </div>
    </div>
  );
}

function RiskBar({ label, pct, warnAt, dangerAt, left, right, testId }) {
  const color = pct >= dangerAt ? "bg-loss" : pct >= warnAt ? "bg-amber-500" : "bg-gain";
  return (
    <div className="mb-4 last:mb-0" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase font-bold tracking-[0.1em] text-gray-500">{label}</p>
        <p className="text-xs font-mono text-gray-600">{right}</p>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${color}`}
          style={{ width: `${Math.max(Math.min(pct, 100), pct > 0 ? 2 : 0)}%` }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">{left}</p>
    </div>
  );
}

function Stat({ label, value, cls = "", testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</p>
      <p className={`font-display text-xl font-medium tabular-nums mt-1 ${cls}`}>{value}</p>
    </div>
  );
}
